// ./src/components/features/CreateEventWizard/components/InvitationQuickSend.tsx
//
// "Send invitations" on the wizard's finish screen (ROADMAP A2b #62 P6c, owner 2026-09-29: "the final step should
// also offer preview, publish and send invitations right from there — see how invitations work on the Invitations
// page").
//
// The Invitations page's own pipeline (`useGuestMessageSend`), compact: everyone on the list NOT INVITED YET (the
// Invitations page's own default audience) by EMAIL, with the message the page would pick for this event type, a
// preview of the first guest's email exactly as it arrives, what it costs against the allowance, and Send behind a
// confirmation. "Customize" opens the full page — another message, a text message, a different audience.
//
// ⚠ Shown only once the event is LIVE (the caller's gate): a reply to a draft is refused, so an invitation sent before
//   publishing is a link nobody can answer.
// ⚠ Every read states its own failure (the list, the dry run, the preview) — never "nobody to invite".

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Anchor, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { AppIcon, ConfirmModal, ErrorAlert, SelectField } from '@/components/generic';
import { TopUpDrawer } from '@/components/features/Billing/TopUpDrawer';
import { useTopUpDrawer } from '@/hooks/useTopUpDrawer';
import { EVENT_RSVP_STATUS, EventGuest, EventGuestService, InvitationSendResult, getGuestEmail } from '@/utils/api';
import { SendCostSummary } from '@/pages/private/EventMessageComposer/SendCostSummary';
import { SendLimitNotice, classifySendRefusal } from '@/pages/private/EventMessageComposer/SendLimitNotice';
import { MessageContent, plainToHtml, useGuestMessageSend } from '@/pages/private/EventMessageComposer/useGuestMessageSend';

export interface InvitationQuickSendProps {
  eventId: string;
  /** After a send — the caller re-reads its counts. */
  onSent: () => void;
  /** Opens the full Invitations page. */
  onCustomize: () => void;
  /**
   * The event is still a DRAFT: the one button PUBLISHES, then sends (owner, 2026-09-29: "what about we do publish & send
   * invites?"). ⚠ Publish comes first and must SUCCEED — a reply to a draft is refused, so a send after a failed publish
   * would hand every guest a link they cannot answer. `blocked` is the premium gate: nothing publishes while it holds.
   */
  publishFirst?: { onPublish: () => Promise<string | null>; blocked: boolean };
}

export const InvitationQuickSend: React.FC<InvitationQuickSendProps> = ({ eventId, onSent, onCustomize, publishFirst }) => {
  const { t } = useTranslation(['events', 'common']);

  // The audience — not invited yet, as the Invitations page opens on.
  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [readError, setReadError] = useState<OpResult | null>(null);
  // The catalog message in use, and its body as the email editor would hold it. Null → the server's own default.
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sent, setSent] = useState<InvitationSendResult | null>(null);
  const [publishing, setPublishing] = useState(false);
  // Why the publish was refused — said HERE, where the host pressed the button.
  const [publishRefused, setPublishRefused] = useState<string | null>(null);

  const pipeline = useGuestMessageSend({
    eventId,
    mode: 'invitation',
    channel: 'email',
    from: 'wizard',
    onCatalogLoaded: ({ messages, recommendedId }) => {
      const pick = messages.find((m) => m.id === recommendedId) ?? messages[0];

      if (pick) {
        setTemplateId(pick.id ?? null);
        setMessage(plainToHtml(pick.body ?? '') || null);
      }
    },
  });
  const { catalog, catalogLoaded, catalogError, preview, previewing, runPreview, rendered, rendering, fetchRendered, sending, send, error, errorSource, linkedAdults } =
    pipeline;

  const loadGuests = useCallback(async () => {
    const res = await EventGuestService.listAll({ eventId: [eventId], rsvpStatus: [EVENT_RSVP_STATUS.UNKNOWN] }, undefined, { maxRows: 2000 });

    // ⚠ Settled on failure too — a failed read shows its error, never a spinner for ever.
    setIsLoaded(true);

    if (!res.didSucceed()) {
      setReadError(res);

      return;
    }

    setReadError(null);
    setGuests(((Array.isArray(res.data) ? res.data : []) as EventGuest[]).filter((g) => !!g.id));
  }, [eventId]);

  useEffect(() => {
    void loadGuests();
  }, [loadGuests]);

  const reachable = useMemo(() => guests.filter((g) => !!getGuestEmail(g)), [guests]);
  const ids = useMemo(() => reachable.map((g) => String(g.id)), [reachable]);
  const noEmail = guests.length - reachable.length;
  const content: MessageContent = useMemo(() => ({ message, messageFormat: 'html', templateId }), [message, templateId]);

  // The dry run and the first guest's rendered email — once the audience and the message are known, and again when
  // the host picks another message.
  const ready = isLoaded && !readError && catalogLoaded;

  const previewNow = useCallback(() => {
    if (ids.length === 0) {
      return;
    }

    void runPreview(ids, content);
    void fetchRendered(ids[0], content);
  }, [ids, content, runPreview, fetchRendered]);

  useEffect(() => {
    if (ready) {
      previewNow();
    }
  }, [ready, previewNow]);

  const topUp = useTopUpDrawer({ eventId, kind: 'email', inTask: true });
  const refusal = useMemo(() => classifySendRefusal(error), [error]);

  const pickMessage = (id: string | null) => {
    const picked = catalog.find((m) => m.id === id);

    if (picked) {
      setTemplateId(picked.id ?? null);
      setMessage(plainToHtml(picked.body ?? '') || null);
    }
  };

  const doSend = async () => {
    setConfirmOpen(false);

    if (publishFirst) {
      setPublishing(true);
      setPublishRefused(null);

      const refused = await publishFirst.onPublish();

      setPublishing(false);

      // ⚠ Not live → NOTHING is sent (a reply to a draft is refused), and the reason is shown right here.
      if (refused) {
        setPublishRefused(refused);

        return;
      }
    }

    const result = await send(ids, content);

    if (!result) {
      return;
    }

    setSent(result);
    onSent();
    await loadGuests();
  };

  // ⚠ The SERVER's count once the dry run is back (it also skips, e.g., an opted-out address); the list's until then.
  const sendable = preview ? preview.withEmail ?? 0 : ids.length;
  const failedPreview = errorSource === 'preview' || errorSource === 'render';

  const renderBody = () => {
    if (!isLoaded) {
      return <Skeleton height={80} radius="md" />;
    }

    if (readError) {
      return (
        <Stack gap="xs">
          <ErrorAlert error={readError} title={t('events:wizard.send.loadFailed', { defaultValue: 'Could not load who to invite' })} page="Create event" operation="eventGuestList" />
          <Group>
            <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void loadGuests()}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (guests.length === 0) {
      return (
        <Text size="md" c="dimmed" ta="left">
          {sent
            ? t('events:wizard.send.allSent', { defaultValue: 'Everyone on your guest list has been invited.' })
            : t('events:wizard.send.nobodyNew', { defaultValue: 'Everyone on your guest list has already been invited.' })}
        </Text>
      );
    }

    return (
      <Stack gap="sm">
        {reachable.length === 0 ? (
          <Text size="md" ta="left">
            {t('events:wizard.send.noneWithEmail', {
              defaultValue: 'Nobody still to invite has an email address — send them a text message from the Invitations page.',
            })}
          </Text>
        ) : (
          <Text size="md" ta="left">
            {sendable === 1
              ? t('events:wizard.send.countOne', { defaultValue: '1 guest will get an email invitation with their own link.' })
              : t('events:wizard.send.count', { defaultValue: '{{count}} guests will get an email invitation, each with their own link.', count: sendable })}
            {/* #62 P6c Phase B — the families' other adults, counted by the server's own dry run (never guessed here). */}
            {preview && linkedAdults
              ? ` ${
                  linkedAdults === 1
                    ? t('events:wizard.send.linkedOne', { defaultValue: 'One more family member gets their own copy, linked to their family’s invitation.' })
                    : t('events:wizard.send.linked', {
                        defaultValue: '{{count}} more family members get their own copies, linked to their family’s invitation.',
                        count: linkedAdults,
                      })
                }`
              : null}
          </Text>
        )}
        {catalogError ? (
          <Text size="md" c="orange.8" ta="left">
            {t('events:wizard.send.catalogFailed', {
              defaultValue: 'Could not load the ready-made messages, so the standard invitation will be sent. Choose or write another on the Invitations page.',
            })}
          </Text>
        ) : null}
        {reachable.length > 0 && noEmail > 0 ? (
          <Text size="md" c="dimmed" ta="left">
            {t('events:wizard.send.noEmail', {
              defaultValue: '{{count}} have no email address — send them a text message from the Invitations page.',
              count: noEmail,
            })}
          </Text>
        ) : null}

        {catalog.length > 1 ? (
          <SelectField
            size="md"
            label={t('events:wizard.send.message', { defaultValue: 'Message' })}
            data={catalog.map((m) => ({ value: m.id as string, label: m.label || m.id || '' }))}
            value={templateId}
            onChange={(_name, v) => pickMessage(v)}
            allowDeselect={false}
            disabled={sending}
          />
        ) : null}

        {reachable.length > 0 ? <SendCostSummary preview={preview} channel="email" onTopUp={() => topUp.open()} balance={topUp.balance} /> : null}

        {reachable.length > 0 && (rendering || rendered) ? (
          rendering || !rendered ? (
            <Skeleton height={220} radius="md" />
          ) : (
            <Stack gap={4}>
              <Text size="md" ta="left">
                <Text span c="dimmed">
                  {t('events:wizard.send.subject', { defaultValue: 'Subject:' })}{' '}
                </Text>
                {rendered.subject}
              </Text>
              <iframe
                title={t('events:wizard.send.previewTitle', { defaultValue: 'Invitation preview' })}
                srcDoc={rendered.html ?? ''}
                sandbox=""
                style={{ width: '100%', height: 420, border: '1px solid var(--mantine-color-default-border)', borderRadius: 8, background: '#ffffff' }}
              />
            </Stack>
          )
        ) : null}
      </Stack>
    );
  };

  return (
    <Card withBorder radius="md" padding="lg" w="100%">
      <Stack gap="md">
        <Text fw={600} size="md" ta="left">
          {t('events:wizard.send.title', { defaultValue: 'Send the invitations' })}
        </Text>

        {sent ? (
          <Alert variant="light" color={sent.failed ? 'yellow' : 'teal'} icon={<AppIcon iconName="Check" size={18} />}>
            <Text size="md" ta="left">
              {sent.failed
                ? t('events:wizard.send.sentSome', { defaultValue: '{{sent}} sent, {{failed}} failed.', sent: sent.sent ?? 0, failed: sent.failed })
                : t('events:wizard.send.sentAll', { defaultValue: '{{sent}} sent.', sent: sent.sent ?? 0 })}
            </Text>
          </Alert>
        ) : null}

        {publishRefused ? (
          <Alert variant="light" color="red" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
            <Text size="md" ta="left">
              {t('events:wizard.send.publishRefused', {
                defaultValue: 'The event could not be published, so no invitations were sent: {{reason}}',
                reason: publishRefused,
              })}
            </Text>
          </Alert>
        ) : null}

        {/* A refused SEND is classified (top up / plan / burst); a failed PREVIEW says so, with its own retry. */}
        {errorSource === 'send' && refusal.kind !== 'other' ? <SendLimitNotice refusal={refusal} onTopUp={() => topUp.open()} /> : null}
        {error && errorSource === 'send' && refusal.kind === 'other' ? (
          <ErrorAlert error={error} title={t('events:wizard.send.failed', { defaultValue: 'Could not send' })} page="Create event" operation="eventGuestSendInvitations" />
        ) : null}
        {error && failedPreview ? (
          <Stack gap="xs">
            <ErrorAlert error={error} title={t('events:wizard.send.previewFailed', { defaultValue: 'Could not preview the invitation — nothing was sent' })} page="Create event" operation="eventGuestSendInvitations" />
            <Group>
              <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={previewNow}>
                {t('common:retry', { defaultValue: 'Try again' })}
              </Button>
            </Group>
          </Stack>
        ) : null}

        {renderBody()}

        <Group justify="space-between" wrap="wrap" gap="sm">
          <Anchor component="button" type="button" size="md" onClick={onCustomize}>
            {t('events:wizard.send.customize', { defaultValue: 'Customize the message or who gets it' })}
          </Anchor>
          {ready && reachable.length > 0 ? (
            <Button
              size="md"
              leftSection={<AppIcon iconName="Send" size={16} />}
              loading={sending || publishing}
              disabled={sendable === 0 || !preview || previewing || !!publishFirst?.blocked}
              onClick={() => setConfirmOpen(true)}
            >
              {publishFirst
                ? t('events:wizard.send.publishAndSend', { defaultValue: 'Publish & send invitations' })
                : t('events:wizard.send.send', { defaultValue: 'Send invitations' })}
            </Button>
          ) : null}
        </Group>
      </Stack>

      <TopUpDrawer {...topUp.drawerProps} />

      <ConfirmModal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void doSend()}
        title={
          publishFirst
            ? t('events:wizard.send.confirmPublishTitle', { defaultValue: 'Publish and send invitations?' })
            : t('events:wizard.send.confirmTitle', { defaultValue: 'Send invitations?' })
        }
        message={
          publishFirst
            ? t('events:wizard.send.confirmPublishMessage', {
                defaultValue: 'The event goes live, then {{count}} guest(s) get an email invitation with their own link.',
                count: sendable,
              })
            : t('events:wizard.send.confirmMessage', {
                defaultValue: 'Send an email invitation with a personal link to {{count}} guest(s).',
                count: sendable,
              })
        }
        confirmLabel={
          publishFirst
            ? t('events:wizard.send.publishAndSend', { defaultValue: 'Publish & send invitations' })
            : t('events:wizard.send.send', { defaultValue: 'Send invitations' })
        }
      />
    </Card>
  );
};
